import { faAbacusRegular, FaIcon } from '@/lib/fa'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { format } from 'date-fns'
import * as pl from 'date-fns/locale/pl'
import * as enLocale from 'date-fns/locale/en'
import React from 'react'
import { axiosInstance } from '@/modules/shared/lib/axios'
import useDriverService from '@/modules/drivers/services/DriverService'
import { generatePath, useNavigate } from 'react-router-dom'
import { driversRoutesMap } from '@/modules/drivers/routes'
import { SettlementPeriodSwitcher } from '@/modules/drivers/shared/settlement/SettlementPeriodSwitcher'

interface EmptyStateProps {
  onCalculate: () => void
  selectedCalculateMonths?: string[]
  onMonthsChange?: (months: string[]) => void
  companyId: string
  driverId: string
}

const EmptyState: React.FC<EmptyStateProps> = ({
  onCalculate,
  selectedCalculateMonths,
  onMonthsChange,
  companyId,
  driverId,
}) => {
  const { t, activeLanguage } = useLocalize()
  const { month, year, setMonth } = useDate()
  const locale = activeLanguage?.code === 'en' ? enLocale : pl
  const { getDriverContracts } = useDriverService()
  const navigate = useNavigate()

  const currentYear = React.useMemo(() => {
    if (month) return parseInt(month.split('-')[0], 10)
    if (year) return parseInt(year, 10)
    return new Date().getFullYear()
  }, [year, month])

  const defaultMonths = React.useMemo(() => (month ? [month] : []), [month])

  const [calculatedMonths, setCalculatedMonths] = React.useState<Set<string>>(
    new Set(),
  )
  const [noContractMonths, setNoContractMonths] = React.useState<Set<string>>(
    new Set(),
  )
  const [isChecking, setIsChecking] = React.useState(true)

  React.useEffect(() => {
    if (!companyId || !driverId) {
      setIsChecking(false)
      return undefined
    }

    let cancelled = false
    const checkCalculatedMonths = async () => {
      setIsChecking(true)
      try {
        const driverIdNum =
          typeof driverId === 'string' ? parseInt(driverId, 10) : driverId

        const [groupedRes, contractsData] = await Promise.all([
          axiosInstance.post(`/api/${companyId}/timesheet/groupedTimesheet`, {
            companyId,
            drivers: [driverIdNum],
            year: currentYear,
            month: month
              ? parseInt(month.split('-')[1], 10)
              : new Date().getMonth() + 1,
            showBonusConfiguration: false,
          }),
          getDriverContracts({ companyId, driverId }),
        ])

        if (cancelled) return

        const rows: any[] = groupedRes.data?.data ?? groupedRes.data ?? []
        const driverRow = Array.isArray(rows)
          ? rows.find((r: any) => String(r.driver?.id) === String(driverIdNum))
          : null

        const calculated = new Set<string>()
        if (driverRow?.calculations && Array.isArray(driverRow.calculations)) {
          driverRow.calculations.forEach((exists: boolean, i: number) => {
            if (exists) {
              calculated.add(`${currentYear}-${String(i + 1).padStart(2, '0')}`)
            }
          })
        }
        setCalculatedMonths(calculated)

        const contracts: any[] = Array.isArray(contractsData)
          ? contractsData
          : ((contractsData as any)?.data ?? [])
        const noContract = new Set<string>()
        for (let m = 1; m <= 12; m++) {
          const monthStart = new Date(currentYear, m - 1, 1)
          const monthEnd = new Date(currentYear, m, 0)
          const hasCoverage = contracts.some((c: any) => {
            const cStart = new Date(c.startDate)
            const cEnd = c.endDate
              ? new Date(c.endDate)
              : new Date(9999, 11, 31)
            return cStart <= monthEnd && cEnd >= monthStart
          })
          if (!hasCoverage) {
            noContract.add(`${currentYear}-${String(m).padStart(2, '0')}`)
          }
        }
        setNoContractMonths(noContract)
      } catch (err) {
        console.error('Error checking calculated months:', err)
      } finally {
        if (!cancelled) setIsChecking(false)
      }
    }

    checkCalculatedMonths()
    return () => {
      cancelled = true
    }
  }, [companyId, driverId, currentYear])

  const activeMonths = selectedCalculateMonths ?? defaultMonths

  const activeSet = React.useMemo(() => new Set(activeMonths), [activeMonths])

  const allMonths = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const monthNum = i + 1
      const key = `${currentYear}-${String(monthNum).padStart(2, '0')}`
      const date = new Date(currentYear, i, 1)
      const label = format(date, 'MMMM', { locale })
      return { key, label }
    })
  }, [currentYear, locale])

  const toggleMonth = React.useCallback(
    (key: string) => {
      if (noContractMonths.has(key)) return
      if (calculatedMonths.has(key)) {
        const baseRoute = generatePath(driversRoutesMap.driverTimesheet, {
          companyId,
          driverId,
        })
        setMonth(key)
        navigate(`${baseRoute}/month/${key}`)
        return
      }

      if (key !== month) {
        const baseRoute = generatePath(driversRoutesMap.driverTimesheet, {
          companyId,
          driverId,
        })
        setMonth(key)
        navigate(`${baseRoute}/month/${key}`)
        return
      }

      // Only the displayed month is calculated, also for longer settlement periods (like WPF).
      // The server adds the other months of the period with CalculateAllMonthsForPeriod.
      onMonthsChange?.([key])
    },
    [
      onMonthsChange,
      calculatedMonths,
      noContractMonths,
      month,
      setMonth,
      navigate,
      companyId,
      driverId,
    ],
  )

  const hasCalculableMonths = activeMonths.some(
    (m) => !calculatedMonths.has(m) && !noContractMonths.has(m),
  )

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-6">
      <div className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-primary/10">
        <FaIcon
          icon={faAbacusRegular}
          className="tw-h-8 tw-w-8 tw-text-primary"
        />
      </div>

      <div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5">
        <p className="tw-text-lg tw-font-semibold tw-text-content-base">
          {t('timesheet.calculate-for-month')}
        </p>
        <p className="tw-text-sm tw-text-content-base/60">
          {t('timesheet.calculate-gross-from-cards')}
        </p>
        <SettlementPeriodSwitcher
          companyId={companyId}
          size="md"
          className="tw-mt-1"
        />
      </div>

      <div className="tw-grid tw-grid-cols-6 tw-gap-2">
        {allMonths.map(({ key, label }) => {
          const isActive = activeSet.has(key)
          const isCalculated = calculatedMonths.has(key)
          const isNoContract = noContractMonths.has(key)

          return (
            <button
              key={key}
              onClick={() => toggleMonth(key)}
              disabled={isChecking || isNoContract}
              className={`tw-relative tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-capitalize tw-transition-all ${
                isChecking
                  ? 'tw-bg-gray-100 tw-text-transparent'
                  : isNoContract
                    ? 'tw-cursor-not-allowed tw-border tw-border-red-200 tw-bg-red-50 tw-text-red-700'
                    : isCalculated
                      ? 'tw-cursor-pointer tw-border tw-border-green-200 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100'
                      : isActive
                        ? 'tw-bg-primary tw-text-white tw-shadow-sm'
                        : 'tw-bg-gray-100 tw-text-content-base/70 hover:tw-bg-primary/20 hover:tw-text-primary'
              }`}
            >
              {label}
              {isChecking && (
                <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                  <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-gray-300 tw-border-t-primary" />
                </span>
              )}
            </button>
          )
        })}
      </div>

      {!isChecking &&
        (calculatedMonths.size > 0 || noContractMonths.size > 0) && (
          <div className="tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-content-base/50">
            {calculatedMonths.size > 0 && (
              <span className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-green-500" />
                {t('timesheet.already-calculated')}
              </span>
            )}
            {noContractMonths.size > 0 && (
              <span className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red-500" />
                {t('timesheet.no-contract')}
              </span>
            )}
          </div>
        )}

      <button
        onClick={onCalculate}
        disabled={activeMonths.length === 0 || !hasCalculableMonths}
        className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-5 tw-py-2.5 tw-shadow-sm tw-transition-opacity hover:tw-opacity-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
      >
        <FaIcon icon={faAbacusRegular} className="tw-text-white" />
        <p className="tw-text-sm tw-font-medium tw-text-white">
          {t('timesheet.calculate-from-data')}
        </p>
      </button>
    </div>
  )
}

export default EmptyState
